/**
 * The binary wire format (CLAUDE.md §6), big-endian:
 *
 *   offset  size  field
 *   0       4     magic = ASCII "TSNP"
 *   4       1     version = 0x01
 *   5       4     kdfIterations (uint32)
 *   9       16    salt
 *   25      12    iv (nonce)
 *   37      N     ciphertext
 *   37+N    16    GCM auth tag
 *
 * The last two rows are contiguous and are exactly what WebCrypto's AES-GCM
 * encrypt returns, so they are stored and read as one span.
 *
 * Plaintext fed to the cipher is gzip(JSON.stringify(snapshot)) — compress
 * BEFORE encrypting; ciphertext does not compress.
 *
 * The content hash is SHA-256 of the plaintext JSON bytes (pre-gzip), NOT of
 * the ciphertext: a fresh random IV per push makes the ciphertext different
 * every time, which would defeat the ETag/304 path.
 */
import {
  DecryptionError,
  IV_BYTES,
  KDF_ITERATIONS_DEFAULT,
  SALT_BYTES,
  TAG_BYTES,
  aesGcmDecrypt,
  aesGcmEncrypt,
  concatBytes,
  deriveKey,
  randomBytes,
  sha256Hex,
  utf8,
} from "./crypto.js";
import { parseSnapshot } from "./schema.js";
import { SNAPSHOT_SCHEMA } from "./types.js";
import type { Snapshot } from "./types.js";

export const ENVELOPE_MAGIC = "TSNP";
const MAGIC_BYTES = Uint8Array.from([0x54, 0x53, 0x4e, 0x50]); // "TSNP"
export const ENVELOPE_VERSION = 0x01;

const OFF_MAGIC = 0;
const OFF_VERSION = 4;
const OFF_ITERATIONS = 5;
const OFF_SALT = 9;
const OFF_IV = OFF_SALT + SALT_BYTES; // 25
export const ENVELOPE_HEADER_BYTES = OFF_IV + IV_BYTES; // 37
const MIN_ENVELOPE_BYTES = ENVELOPE_HEADER_BYTES + TAG_BYTES; // 53

/** Guards against an absurd header value forcing a multi-minute key derivation. */
const MAX_KDF_ITERATIONS = 10_000_000;

export class EnvelopeError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "EnvelopeError";
  }
}

/** Raised when a blob was written by a newer app than this one understands. */
export class SchemaTooNewError extends Error {
  readonly found: number;
  readonly known: number;
  constructor(found: number) {
    super(
      `snapshot schema ${found} is newer than this app understands (${SNAPSHOT_SCHEMA}) — update the app`,
    );
    this.name = "SchemaTooNewError";
    this.found = found;
    this.known = SNAPSHOT_SCHEMA;
  }
}

export type EnvelopeParts = {
  version: number;
  kdfIterations: number;
  salt: Uint8Array;
  iv: Uint8Array;
  /** ciphertext with the GCM tag appended */
  ciphertext: Uint8Array;
};

export function encodeEnvelope(
  parts: Omit<EnvelopeParts, "version"> & { version?: number },
): Uint8Array {
  const { kdfIterations, salt, iv, ciphertext } = parts;
  const version = parts.version ?? ENVELOPE_VERSION;

  if (salt.length !== SALT_BYTES) {
    throw new EnvelopeError(`salt must be ${SALT_BYTES} bytes, got ${salt.length}`);
  }
  if (iv.length !== IV_BYTES) {
    throw new EnvelopeError(`iv must be ${IV_BYTES} bytes, got ${iv.length}`);
  }
  if (
    !Number.isInteger(kdfIterations) ||
    kdfIterations < 1 ||
    kdfIterations > MAX_KDF_ITERATIONS
  ) {
    throw new EnvelopeError(`kdfIterations out of range: ${kdfIterations}`);
  }
  if (ciphertext.length < TAG_BYTES) {
    throw new EnvelopeError("ciphertext is shorter than the GCM tag");
  }

  const header = new Uint8Array(ENVELOPE_HEADER_BYTES);
  header.set(MAGIC_BYTES, OFF_MAGIC);
  header[OFF_VERSION] = version;
  new DataView(header.buffer).setUint32(OFF_ITERATIONS, kdfIterations, false); // big-endian
  header.set(salt, OFF_SALT);
  header.set(iv, OFF_IV);

  return concatBytes(header, ciphertext);
}

export function decodeEnvelope(blob: Uint8Array): EnvelopeParts {
  if (blob.length < MIN_ENVELOPE_BYTES) {
    throw new EnvelopeError(
      `truncated envelope: ${blob.length} bytes, minimum is ${MIN_ENVELOPE_BYTES}`,
    );
  }
  for (let i = 0; i < MAGIC_BYTES.length; i++) {
    if (blob[OFF_MAGIC + i] !== MAGIC_BYTES[i]) {
      throw new EnvelopeError("not a Tally Pocket snapshot (bad magic)");
    }
  }
  const version = blob[OFF_VERSION] as number;
  if (version !== ENVELOPE_VERSION) {
    throw new EnvelopeError(`unsupported envelope version ${version}`);
  }

  const view = new DataView(blob.buffer, blob.byteOffset, blob.byteLength);
  const kdfIterations = view.getUint32(OFF_ITERATIONS, false);
  if (kdfIterations < 1 || kdfIterations > MAX_KDF_ITERATIONS) {
    throw new EnvelopeError(`kdfIterations out of range: ${kdfIterations}`);
  }

  return {
    version,
    kdfIterations,
    salt: blob.slice(OFF_SALT, OFF_SALT + SALT_BYTES),
    iv: blob.slice(OFF_IV, OFF_IV + IV_BYTES),
    ciphertext: blob.slice(ENVELOPE_HEADER_BYTES),
  };
}

/* ---------------------------------------------------------------- gzip ---
 * Compression/DecompressionStream are native in Node 18+ AND every target
 * browser, so one implementation serves both runtimes and `shared` keeps its
 * zero-runtime-deps rule (zod excepted).
 */

/** Matches what CompressionStream/DecompressionStream actually are: BufferSource in, Uint8Array out. */
type ByteTransform = {
  readable: ReadableStream<Uint8Array>;
  writable: WritableStream<BufferSource>;
};

async function throughStream(
  bytes: Uint8Array,
  stream: ByteTransform,
): Promise<Uint8Array> {
  const writer = stream.writable.getWriter();
  // Swallowed deliberately: when the stream rejects (bad gzip data) BOTH sides
  // reject. The reader's rejection is the one we propagate; leaving the
  // writer's unhandled would surface as an unhandled rejection instead.
  const writing = (async () => {
    try {
      await writer.write(bytes as BufferSource);
      await writer.close();
    } catch {
      /* reported via the reader below */
    }
  })();

  try {
    const chunks: Uint8Array[] = [];
    const reader = stream.readable.getReader();
    for (;;) {
      const { done, value } = await reader.read();
      if (done) break;
      if (value) chunks.push(value);
    }
    return concatBytes(...chunks);
  } finally {
    await writing;
  }
}

export async function gzip(bytes: Uint8Array): Promise<Uint8Array> {
  return throughStream(bytes, new CompressionStream("gzip"));
}

export async function gunzip(bytes: Uint8Array): Promise<Uint8Array> {
  try {
    return await throughStream(bytes, new DecompressionStream("gzip"));
  } catch {
    throw new DecryptionError("decrypted payload was not valid gzip data");
  }
}

/* ------------------------------------------------------------ seal/open --- */

export type SealedSnapshot = {
  /** the bytes to POST to /v1/push */
  blob: Uint8Array;
  /** SHA-256 hex of the plaintext JSON bytes — the ETag and the change gate */
  hash: string;
  generatedAt: string;
  schema: number;
  /** size of the UTF-8 JSON before gzip, useful for logging */
  jsonBytes: number;
  /** size of the envelope actually pushed — this is what KV stores */
  blobBytes: number;
  kdfIterations: number;
};

export async function sealSnapshot(
  snapshot: Snapshot,
  passphrase: string,
  opts: { kdfIterations?: number } = {},
): Promise<SealedSnapshot> {
  const kdfIterations = opts.kdfIterations ?? KDF_ITERATIONS_DEFAULT;

  const jsonBytes = utf8.encode(JSON.stringify(snapshot));
  const hash = await sha256Hex(jsonBytes);

  const compressed = await gzip(jsonBytes);
  const salt = randomBytes(SALT_BYTES);
  const iv = randomBytes(IV_BYTES);
  const key = await deriveKey(passphrase, salt, kdfIterations);
  const ciphertext = await aesGcmEncrypt(key, iv, compressed);

  const blob = encodeEnvelope({ kdfIterations, salt, iv, ciphertext });

  return {
    blob,
    hash,
    generatedAt: snapshot.generatedAt,
    schema: snapshot.schema,
    jsonBytes: jsonBytes.length,
    blobBytes: blob.length,
    kdfIterations,
  };
}

export type OpenedSnapshot = {
  snapshot: Snapshot;
  /** recomputed from the plaintext — compare against the ETag/meta hash */
  hash: string;
};

export async function openSnapshotWithHash(
  blob: Uint8Array,
  passphrase: string,
): Promise<OpenedSnapshot> {
  const { kdfIterations, salt, iv, ciphertext } = decodeEnvelope(blob);
  const key = await deriveKey(passphrase, salt, kdfIterations);
  const compressed = await aesGcmDecrypt(key, iv, ciphertext);
  const jsonBytes = await gunzip(compressed);
  const hash = await sha256Hex(jsonBytes);

  let raw: unknown;
  try {
    raw = JSON.parse(utf8.decode(jsonBytes));
  } catch {
    throw new DecryptionError("decrypted payload was not valid JSON");
  }

  // Check the version before zod does, so a newer snapshot produces an
  // actionable "update the app" rather than a wall of literal mismatches.
  const found = (raw as { schema?: unknown }).schema;
  if (typeof found === "number" && found > SNAPSHOT_SCHEMA) {
    throw new SchemaTooNewError(found);
  }

  return { snapshot: parseSnapshot(raw), hash };
}

export async function openSnapshot(
  blob: Uint8Array,
  passphrase: string,
): Promise<Snapshot> {
  return (await openSnapshotWithHash(blob, passphrase)).snapshot;
}
